import * as React from 'react';

interface INewAppToastProps {
  /**
   * Full federation entry URL the CTA links to. For zero-prompt seamless login this is the
   * Cognito Hosted-UI `/oauth2/authorize` URL carrying `identity_provider=LegacyApp` (D5 / AVR-206) —
   * the new app has no `/login` route of its own (auth is handled by the ALB + Cognito). Built
   * server-side in website/scripts.php so the URL/params stay configurable per environment.
   */
  loginUrl: string;
  /** localStorage key used to remember the "don't show again" choice. */
  storageKey?: string;
}

const DEFAULT_STORAGE_KEY = 'avannis_new_app_toast_dismissed';

/**
 * Feature-flagged "try the new app" toast for the legacy monolith (AVR-184 / Phase 2, Door 1).
 * Mounted globally from website/scripts.php only when the server-side flag is on. Presentational
 * only — all gating (whether to show, rollout, the target URL) is decided server-side.
 */
export const NewAppToast: React.FC<INewAppToastProps> = ({ loginUrl, storageKey = DEFAULT_STORAGE_KEY }) => {
  const wasDismissed = (): boolean => {
    try {
      return window.localStorage.getItem(storageKey) === 'true';
    } catch (e) {
      return false;
    }
  };

  const [visible, setVisible] = React.useState<boolean>(!wasDismissed());

  if (!visible || !loginUrl) {
    return null;
  }

  // Close (×) hides only for this render; the toast reappears on the next page load.
  // Use "Don't show again" (dismiss(true)) to persist the dismissal via localStorage.
  const dismiss = (remember: boolean) => {
    if (remember) {
      try {
        window.localStorage.setItem(storageKey, 'true');
      } catch (e) {
        // Storage unavailable (private mode / quota) — just hide for this session.
      }
    }
    setVisible(false);
  };

  return (
    <div className="new-app-toast" role="status">
      <span className="new-app-toast__message">A faster Avannis Reports is ready.</span>
      <a className="new-app-toast__cta" href={loginUrl}>Try the new app</a>
      <button type="button" className="new-app-toast__dismiss" onClick={() => dismiss(true)}>
        Don&apos;t show again
      </button>
      <button type="button" className="new-app-toast__close" aria-label="Close" onClick={() => dismiss(false)}>
        &times;
      </button>
    </div>
  );
};
